// src/pages/LiveTracking.js

import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  RefreshCw,
  Navigation,
  BatteryMedium,
  Users,
  Clock,
  LocateFixed,
} from "lucide-react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Tooltip,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import api from "../constant/api";
import { useMenuPermission } from "../hooks/useMenuPermission";

const MENU_ID = 20;

const DEFAULT_CENTER = [8.7139, 77.7567];

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// --------------------------------------------------
// MAP FOCUS COMPONENT
// --------------------------------------------------

function MapFocus({ salesman }) {
  const map = useMap();

  useEffect(() => {
    if (!salesman?.location) return;

    const lat = Number(salesman.location.latitude);
    const lng = Number(salesman.location.longitude);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    map.flyTo([lat, lng], 17, {
      duration: 1,
    });
  }, [salesman, map]);

  return null;
}

// --------------------------------------------------
// MAP BOUNDS COMPONENT
// --------------------------------------------------

function FitBounds({ salesmen }) {
  const map = useMap();

  useEffect(() => {
    const validLocations = salesmen
      .map((s) => {
        if (!s.location) return null;

        const lat = Number(s.location.latitude);
        const lng = Number(s.location.longitude);

        if (
          !Number.isFinite(lat) ||
          !Number.isFinite(lng)
        ) {
          return null;
        }

        return [lat, lng];
      })
      .filter(Boolean);

    if (validLocations.length === 0) return;

    if (validLocations.length === 1) {
      map.setView(validLocations[0], 15);
      return;
    }

    const bounds = L.latLngBounds(validLocations);

    map.fitBounds(bounds, {
      padding: [50, 50],
      maxZoom: 15,
    });
  }, [salesmen, map]);

  return null;
}

// --------------------------------------------------
// MAIN COMPONENT
// --------------------------------------------------

export default function LiveTracking() {
  const { loading } = useMenuPermission(MENU_ID);

  const [salesmen, setSalesmen] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSalesman, setSelectedSalesman] = useState(null);

  // --------------------------------------------------
  // LOAD LOCATIONS
  // --------------------------------------------------

  const user = JSON.parse(localStorage.getItem("user") || "{}");

const user_id = user.user_id;
const role = user.role;

  const load = useCallback(async () => {
    try {
      setRefreshing(true);

    const res = await api.get(
  `/Salesman/getLatestLocations?user_id=${user_id}&role=${role}`
);

      if (res.data.success) {
        setSalesmen(res.data.data || []);
      } else {
        setSalesmen([]);
      }
    } catch (error) {
      console.log(
        "Get Latest Locations Error:",
        error
      );
    } finally {
      setRefreshing(false);
    }
  }, []);

  // --------------------------------------------------
  // INITIAL LOAD
  // --------------------------------------------------

  useEffect(() => {
    load();

    const interval = setInterval(() => {
      load();
    }, 30000);

    return () => clearInterval(interval);
  }, [load]);

  // --------------------------------------------------
  // TIME AGO
  // --------------------------------------------------

  const timeAgo = (timestamp) => {
    if (!timestamp) {
      return "No data yet";
    }

    const time = new Date(timestamp).getTime();

    if (Number.isNaN(time)) {
      return "Unknown";
    }

    const diffMinutes = Math.round(
      (Date.now() - time) / 60000
    );

    if (diffMinutes < 1) {
      return "Just now";
    }

    if (diffMinutes < 60) {
      return `${diffMinutes} min ago`;
    }

    const hours = Math.round(
      diffMinutes / 60
    );

    return `${hours} hr ago`;
  };

  // --------------------------------------------------
  // GET LOCATION
  // --------------------------------------------------

  const getPosition = (salesman) => {
    if (!salesman?.location) {
      return null;
    }

    const lat = Number(
      salesman.location.latitude
    );

    const lng = Number(
      salesman.location.longitude
    );

    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lng)
    ) {
      return null;
    }

    return [lat, lng];
  };

  // --------------------------------------------------
  // FOCUS SALESMAN
  // --------------------------------------------------

  const focusSalesman = (salesman) => {
    if (!getPosition(salesman)) {
      return;
    }

    setSelectedSalesman(salesman);
  };

  // --------------------------------------------------
  // PERMISSION LOADING
  // --------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500 font-medium">
          Checking permissions...
        </p>
      </div>
    );
  }

  const onlineSalesmen = salesmen.filter(
    (s) => getPosition(s)
  );

  return (
    <div className="p-2 md:p-4">

      {/* HEADER */}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">

        <div>
          <div className="flex items-center gap-3">

            <div className="w-12 h-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-lg">
              <Navigation size={23} />
            </div>

            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                Live Tracking
              </h1>

              <p className="text-sm text-gray-500 mt-1">
                Track salesman locations in real time
              </p>
            </div>

          </div>
        </div>

        <div className="flex items-center gap-2">

          <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-2 rounded-xl text-xs font-semibold">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            LIVE
          </div>

          <button
            onClick={load}
            disabled={refreshing}
            className="
              flex items-center gap-2
              bg-gray-900 hover:bg-black
              text-white
              px-4 py-2.5
              rounded-xl
              shadow
              text-sm
              font-semibold
              active:scale-95
              transition
              disabled:opacity-50
            "
          >
            <RefreshCw
              size={15}
              className={
                refreshing
                  ? "animate-spin"
                  : ""
              }
            />

            Refresh
          </button>

        </div>
      </div>

      {/* STATS */}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">

          <div className="flex items-center gap-3">

            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <Users size={19} />
            </div>

            <div>
              <p className="text-xs text-gray-400">
                Salesmen
              </p>

              <p className="text-xl font-bold text-gray-800">
                {salesmen.length}
              </p>
            </div>

          </div>

        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">

          <div className="flex items-center gap-3">

            <div className="w-10 h-10 rounded-xl bg-green-50 text-green-600 flex items-center justify-center">
              <MapPin size={19} />
            </div>

            <div>
              <p className="text-xs text-gray-400">
                Locations
              </p>

              <p className="text-xl font-bold text-green-600">
                {onlineSalesmen.length}
              </p>
            </div>

          </div>

        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">

          <div className="flex items-center gap-3">

            <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center">
              <Clock size={19} />
            </div>

            <div>
              <p className="text-xs text-gray-400">
                Auto Refresh
              </p>

              <p className="text-xl font-bold text-orange-600">
                30s
              </p>
            </div>

          </div>

        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">

          <div className="flex items-center gap-3">

            <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
              <LocateFixed size={19} />
            </div>

            <div>
              <p className="text-xs text-gray-400">
                Map
              </p>

              <p className="text-xl font-bold text-purple-600">
                Free
              </p>
            </div>

          </div>

        </div>

      </div>

      {/* MAP */}

      <div className="
        bg-white
        rounded-2xl
        shadow
        border border-gray-100
        overflow-hidden
        mb-5
      ">

        <div className="
          px-4 py-3
          border-b
          bg-gray-50
          flex
          items-center
          justify-between
        ">

          <div className="flex items-center gap-2">

            <MapPin
              size={18}
              className="text-blue-600"
            />

            <span className="font-bold text-gray-800">
              Salesman Locations
            </span>

          </div>

          <span className="text-xs text-gray-400">
            OpenStreetMap
          </span>

        </div>

        <MapContainer
          center={DEFAULT_CENTER}
          zoom={11}
          scrollWheelZoom={true}
          style={{
            width: "100%",
            height: "600px",
          }}
        >

          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <FitBounds
            salesmen={onlineSalesmen}
          />

          {selectedSalesman && (
            <MapFocus
              salesman={selectedSalesman}
            />
          )}

          {onlineSalesmen.map((s) => {

            const position =
              getPosition(s);

            if (!position) {
              return null;
            }

            return (
              <Marker
                key={
                  s.admin_id ||
                  s.user_id ||
                  s.id
                }
                position={position}
                eventHandlers={{
                  click: () => {
                    setSelectedSalesman(s);
                  },
                }}
              >
                 {/* SALESMAN NAME ON MAP */}
  <Tooltip
    permanent
    direction="top"
    offset={[0, -30]}
    opacity={1}
    className="salesman-map-tooltip"
  >
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />

      <span className="font-bold text-gray-800">
        {s.name || "Salesman"}
      </span>
    </div>
  </Tooltip>

                <Popup>

                  <div className="min-w-[220px]">

                    <div className="flex items-center gap-3 mb-3">

                      <div className="
                        w-10 h-10
                        rounded-full
                        bg-blue-600
                        text-white
                        flex
                        items-center
                        justify-center
                        font-bold
                      ">
                        {s.name
                          ?.charAt(0)
                          ?.toUpperCase() || "S"}
                      </div>

                      <div>

                        <p className="font-bold text-gray-800">
                          {s.name ||
                            "Salesman"}
                        </p>

                        <p className="text-xs text-gray-500">
                          {s.employee_code ||
                            "Salesman"}
                        </p>

                      </div>

                    </div>

                    <div className="space-y-2 text-xs text-gray-600">

                      <div className="flex justify-between gap-4">
                        <span>
                          Latitude
                        </span>

                        <b>
                          {position[0].toFixed(
                            6
                          )}
                        </b>
                      </div>

                      <div className="flex justify-between gap-4">
                        <span>
                          Longitude
                        </span>

                        <b>
                          {position[1].toFixed(
                            6
                          )}
                        </b>
                      </div>

                      <div className="flex justify-between gap-4">
                        <span>
                          Updated
                        </span>

                        <b>
                          {timeAgo(
                            s.location
                              ?.recorded_at
                          )}
                        </b>
                      </div>

                      {s.location
                        ?.battery_level !=
                        null && (

                        <div className="flex justify-between gap-4">

                          <span className="flex items-center gap-1">
                            <BatteryMedium
                              size={13}
                            />

                            Battery
                          </span>

                          <b>
                            {
                              s.location
                                .battery_level
                            }%
                          </b>

                        </div>
                      )}

                    </div>

                    <a
                      href={`https://www.openstreetmap.org/?mlat=${position[0]}&mlon=${position[1]}#map=17/${position[0]}/${position[1]}`}
                      target="_blank"
                      rel="noreferrer"
                      className="
                        mt-3
                        block
                        text-center
                        bg-blue-600
                        hover:bg-blue-700
                        text-white
                        py-2
                        rounded-lg
                        text-xs
                        font-semibold
                      "
                    >
                      Open Map
                    </a>

                  </div>

                </Popup>

              </Marker>
            );
          })}

        </MapContainer>

      </div>

      {/* SALESMAN CARDS */}

      <div className="mb-3 flex items-center justify-between">

        <h2 className="font-bold text-gray-800">
          Salesmen
        </h2>

        <span className="text-xs text-gray-400">
          Updates every 30 seconds
        </span>

      </div>

      <div className="
        grid
        grid-cols-1
        sm:grid-cols-2
        lg:grid-cols-3
        gap-4
      ">

        {salesmen.map((s) => {

          const position =
            getPosition(s);

          const online =
            Boolean(position);

          return (
            <motion.div
              key={
                s.admin_id ||
                s.user_id ||
                s.id
              }
              initial={{
                opacity: 0,
                y: 15,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              className="
                bg-white
                rounded-2xl
                shadow
                border
                border-gray-100
                p-4
              "
            >

              <div className="flex items-center justify-between">

                <div className="flex items-center gap-3">

                  <div className="relative">

                    <div className="
                      w-11 h-11
                      rounded-full
                      bg-gradient-to-br
                      from-blue-500
                      to-indigo-600
                      text-white
                      flex
                      items-center
                      justify-center
                      font-bold
                    ">
                      {s.name
                        ?.charAt(0)
                        ?.toUpperCase() ||
                        "S"}
                    </div>

                    {online && (
                      <span className="
                        absolute
                        bottom-0
                        right-0
                        w-3
                        h-3
                        bg-green-500
                        border-2
                        border-white
                        rounded-full
                      " />
                    )}

                  </div>

                  <div className="min-w-0">

                    <p className="
                      font-semibold
                      text-gray-800
                      truncate
                    ">
                      {s.name ||
                        "Salesman"}
                    </p>

                    <p className="text-xs text-gray-400">
                      {s.employee_code ||
                        "—"}
                    </p>

                  </div>

                </div>

                <span
                  className={`
                    text-[10px]
                    px-2
                    py-1
                    rounded-full
                    font-bold
                    ${
                      online
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-500"
                    }
                  `}
                >
                  {online
                    ? "ONLINE"
                    : "OFFLINE"}
                </span>

              </div>

              <div className="
                mt-3
                flex
                items-center
                justify-between
                text-xs
                text-gray-500
              ">

                <span className="
                  flex
                  items-center
                  gap-1
                ">
                  <MapPin size={13} />

                  {timeAgo(
                    s.location
                      ?.recorded_at
                  )}
                </span>

                {s.location
                  ?.battery_level !=
                  null && (

                  <span className="
                    flex
                    items-center
                    gap-1
                  ">
                    <BatteryMedium
                      size={13}
                    />

                    {
                      s.location
                        .battery_level
                    }%
                  </span>
                )}

              </div>

              {online ? (

                <div className="grid grid-cols-2 gap-2 mt-3">

                  <button
                    onClick={() =>
                      focusSalesman(s)
                    }
                    className="
                      flex
                      items-center
                      justify-center
                      gap-2
                      bg-blue-50
                      hover:bg-blue-100
                      text-blue-700
                      font-semibold
                      text-sm
                      py-2.5
                      rounded-xl
                      transition
                    "
                  >
                    <LocateFixed
                      size={14}
                    />

                    Locate
                  </button>

                  <a
                    href={`https://www.openstreetmap.org/?mlat=${position[0]}&mlon=${position[1]}#map=17/${position[0]}/${position[1]}`}
                    target="_blank"
                    rel="noreferrer"
                    className="
                      flex
                      items-center
                      justify-center
                      gap-2
                      bg-gray-900
                      hover:bg-black
                      text-white
                      font-semibold
                      text-sm
                      py-2.5
                      rounded-xl
                      transition
                    "
                  >
                    <Navigation
                      size={14}
                    />

                    Map
                  </a>

                </div>

              ) : (

                <div className="
                  mt-3
                  text-center
                  text-xs
                  text-gray-400
                  bg-gray-50
                  py-2.5
                  rounded-xl
                ">
                  No location shared yet
                </div>

              )}

            </motion.div>
          );
        })}

        {salesmen.length === 0 && (

          <div className="
            col-span-full
            text-center
            py-16
            text-gray-400
          ">

            <MapPin
              className="mx-auto mb-2"
              size={32}
            />

            <p>
              No salesman locations found.
            </p>

          </div>

        )}

      </div>

    </div>
  );
}